import { ParaviewConfig } from "@/types/dataset";
import i18n from "@/utils/i18n";
import { defineStore } from "pinia";
import { useHierarchyStore } from "../hierarchyStore";
import { usePanelStore } from "../panelStore";
import { useToastStore as toast } from "../toastStore";
import { useActiveStore as active } from "./active";
import { useDisplayPaneStore } from "./displayPane";
import { useLayerStore as layer } from "./layer";
import { useSaveStateStore } from "./saveState";
import { useViewStore as views } from "./views";
import { useFiltersStore } from "./filters";

const SUBSCRIPTIONS = {} as any;
// ----------------------------------------------------------------------------
export const usePvwStore = defineStore("pvwStore", {
	state: () =>
		({
			client: null,
			paraviewConfig: null as ParaviewConfig | null,
			repSubscription: null,
			layerSubscription: null,
			layerRepSubscription: null,
			fieldColoringSubscription: null
		}) as any,
	getters: {
		PVW_CLIENT(): any {
			return this.client;
		},
		PVW_MINE_CONFIG(): ParaviewConfig {
			return this.paraviewConfig;
		}
	},
	actions: {
		PVW_CLIENT_SET(client: any) {
			this.client = client;
		},
		// ------------------------------------------------------------------------
		// Data Management
		// ------------------------------------------------------------------------
		PVW_LOAD_MINE(name: string) {
			return this.client
				.getRemote()
				.DataCloud.loadMine(name)
				.catch(
					(e: any) => {
						toast().toast({
							message: "",
							title: i18n.global.t("toastGeneralError"),
							type: "error",
							dismissable: false
						});
						console.log(e);
					}
					// dispatch('PUSH_APP_ERROR', { message: 'There was a problem loading mine configuration.' })
				);
		},
		// ------------------------------------------------------------------------
		async PVW_CONFIGURE_MINE(config: ParaviewConfig) {
			this.paraviewConfig = config;
			return (
				this.client &&
				this.client
					.getRemote()
					.DataCloud.configureMine(config)
					.catch((e: any) => {
						console.log(`file: pvw.ts:139 ~ e:`, e);
						// handle error
					})
			);
		},
		// ------------------------------------------------------------------------
		// blobFilePathObj == layerName
		async PVW_LOAD_DATASET(viewId: string, blobFilePathObj: any) {
			return (
				this.client &&
				this.client
					.getRemote()
					.DataCloud.loadPieces(viewId, blobFilePathObj)
					.catch((e: any) => {
						console.log(`file: pvw.ts:155 ~ e:`, e);
						// handle error
					})
			);
		},
		// ------------------------------------------------------------------------
		PVW_HISTOGRAM_FETCH({
			layerName,
			viewId,
			field,
			dataRange,
			numberOfSamples = 100
		}: {
			layerName: string;
			viewId: string;
			field: string;
			dataRange: any;
			numberOfSamples: number;
		}) {
			// # todo - check if layer selected in display pane before calling
			//if(netGuard(['getHistogram', layerName, viewId,field],[dataRange[0],dataRange[1],numberOfSamples])){
			return this.client
				.getRemote()
				.DataCloud.getHistogram(layerName, viewId, field, dataRange, numberOfSamples)
				.catch(() => {
					console.log("prob fetching histogram");
					//toast().toast({message:'There was a problem fetching histogram.',title:'An error occured.', type:'error', dismissable:false})
				});
			/*}else{
        console.log("catch netguard",['getHistogram', layerName, viewId],[field,dataRange[0],dataRange[1],numberOfSamples])
      }*/
		},
		// ------------------------------------------------------------------------
		//updateActiveLayerInView
		async PVW_UPDATE_LAYER_VIEW(viewId: string, blobFilePath: any) {
			if (blobFilePath == null) return;
			return (
				this.client &&
				this.client
					.getRemote()
					.DataCloud.updateActiveLayerInView(viewId, blobFilePath)
					.catch((e: any) => {
						console.log(`file: pvw.ts:155 ~ e:`, e);
						// handle error
						toast().toast({
							message: i18n.global.t("storePvwToasts.errorUpdatingLayer"),
							title: i18n.global.t("toastGeneralError"),
							type: "error",
							dismissable: false
						});
					})
			);
		},
		PVW_SUBSCRIBE_VIEW_STATE(callback: any) {
			if (SUBSCRIPTIONS.view) {
				console.error("PVW_SUBSCRIBE_VIEW_STATE already called", SUBSCRIPTIONS.view);
				return;
			}
			return this.client
				.getRemote()
				.DataCloud.subscribeToViewState(callback)
				.then((subscription: any) => {
					SUBSCRIPTIONS.view = subscription;
				})
				.catch(() => {
					toast().toast({
						message: i18n.global.t("storePvwToasts.errorSubState"),
						title: i18n.global.t("toastGeneralError"),
						type: "error",
						dismissable: false
					});
				});
		},
		PVW_UNSUBSCRIBE_VIEW_STATE() {
			if (!SUBSCRIPTIONS.view) {
				return;
			}
			const subscription = SUBSCRIPTIONS.view;
			SUBSCRIPTIONS.view = null;
			return this.client
				.getRemote()
				.DataCloud.unsubscribeToViewState(subscription)
				.catch(() => {
					toast().toast({
						message: i18n.global.t("storePvwToasts.errorUnsubState"),
						title: i18n.global.t("toastGeneralError"),
						type: "error",
						dismissable: false
					});
				});
		},
		PVW_SUBSCRIBE_LAYER_STATE(callback: any) {
			if (SUBSCRIPTIONS.layer) {
				console.error("PVW_SUBSCRIBE_LAYER_STATE already called", SUBSCRIPTIONS.layer);
				return;
			}
			return this.client
				.getRemote()
				.DataCloud.subscribeToLayerState(callback)
				.then((subscription: any) => {
					SUBSCRIPTIONS.layer = subscription;
				})
				.catch(() => {
					toast().toast({
						message: i18n.global.t("storePvwToasts.errorSubLayerState"),
						title: i18n.global.t("toastGeneralError"),
						type: "error",
						dismissable: false
					});
				});
		},
		PVW_UNSUBSCRIBE_LAYER_STATE() {
			if (!SUBSCRIPTIONS.layer) {
				return;
			}
			const subscription = SUBSCRIPTIONS.layer;
			SUBSCRIPTIONS.layer = null;
			return this.client
				.getRemote()
				.DataCloud.unsubscribeToLayerState(subscription)
				.catch(() => {
					toast().toast({
						message: i18n.global.t("storePvwToasts.errorUnsubLayerState"),
						title: i18n.global.t("toastGeneralError"),
						type: "error",
						dismissable: false
					});
				});
		},
		PVW_SUBSCRIBE_REPRESENTATION_STATE(callback: any) {
			if (SUBSCRIPTIONS.representation) {
				console.error(
					"PVW_SUBSCRIBE_REPRESENTATION_STATE already called",
					SUBSCRIPTIONS.representation
				);
				return;
			}
			return this.client
				.getRemote()
				.DataCloud.subscribeToRepresentationState(callback)
				.then((subscription: any) => {
					SUBSCRIPTIONS.representation = subscription;
				})
				.catch(() => {
					toast().toast({
						message: i18n.global.t("storePvwToasts.errorSubRepState"),
						title: i18n.global.t("toastGeneralError"),
						type: "error",
						dismissable: false
					});
				});
		},
		PVW_UNSUBSCRIBE_REPRESENTATION_STATE() {
			if (!SUBSCRIPTIONS.representation) {
				return;
			}
			const subscription = SUBSCRIPTIONS.representation;
			SUBSCRIPTIONS.representation = null;
			return this.client
				.getRemote()
				.DataCloud.unsubscribeToRepresentationState(subscription)
				.catch(() => {
					toast().toast({
						message: i18n.global.t("storePvwToasts.errorUnsubRepState"),
						title: i18n.global.t("toastGeneralError"),
						type: "error",
						dismissable: false
					});
				});
		},
		async PVW_PUSH_SERVER_STATE() {
			return this.client
				.getRemote()
				.DataCloud.pushServerState()
				.catch(() => {
					toast().toast({
						message: i18n.global.t("storePvwToasts.errorPushServerState"),
						title: i18n.global.t("toastGeneralError"),
						type: "error",
						dismissable: false
					});
				});
		},
		// ------------------------------------------------------------------------
		// View Management
		// ------------------------------------------------------------------------
		async PVW_LAYOUT_GET() {
			return this.client
				.getRemote()
				.DataCloud.getLayout()
				.catch(() => {
					toast().toast({
						message: i18n.global.t("storePvwToasts.errorGettingLayout"),
						title: i18n.global.t("toastGeneralError"),
						type: "error",
						dismissable: false
					});
				});
		},
		// ------------------------------------------------------------------------
		async PVW_LAYOUT_SET(layout: any) {
			return this.client
				.getRemote()
				.DataCloud.setLayout(layout)
				.catch(() => {
					toast().toast({
						message: i18n.global.t("storePvwToasts.errorSettingLayout"),
						title: i18n.global.t("toastGeneralError"),
						type: "error",
						dismissable: false
					});
				});
		},

		async PVW_ADD_VIEW({ viewType }: { viewType: string; id: number | null }) {
			return this.client
				.getRemote()
				.DataCloud.addView(viewType)
				.then((viewIds: any) => {
					const newId = viewIds.find(
						(el: any) => views().SSA_VIEW_IDS.indexOf(el) === -1
					);
					views().SSA_VIEW_IDS_SET(viewIds);
					// set any new view to active. Default colormap depends on this.
					if (newId) {
						active().SSA_ACTIVE_VIEW_SET(newId);
					}
					return newId;
				})
				.catch(() => {
					toast().toast({
						message: i18n.global.t("storePvwToasts.errorAddingView"),
						title: i18n.global.t("toastGeneralError"),
						type: "error",
						dismissable: false
					});

					//dispatch('PUSH_APP_ERROR', { message: 'There was a problem adding view.' })
				});
		},
		async PVW_REMOVE_VIEW({ viewId }: { viewId: string | number }) {
			this.PVW_VIEW_LINK({ viewId, link: false });
			usePanelStore().SET_READY(false);
			return this.client
				.getRemote()
				.DataCloud.removeView(viewId)
				.then((viewIds: any) => {
					const vIds = views().SSA_VIEW_IDS;
					const viewStates = views().SSA_VIEW_STATES;
					for (let i = 0; i < vIds.length; i++) {
						if (viewIds.indexOf(vIds[i]) < 0) {
							this.PVW_VIEW_LINK({ viewId: viewIds[i], link: false });
							this.PVW_REMOVE_VIEW({ viewId: vIds[i] });
						}
					}
					delete viewStates[viewId];
					views().SSA_VIEW_STATE_UPDATE(viewStates);
					usePanelStore().SET_ACTIVE_VISUALIZER(
						usePanelStore().visualizers[0],
						viewIds[0]
					);

					useHierarchyStore().REMOVE_HIERARCHY_STATE(viewId);
					views().SSA_VIEW_IDS_SET(viewIds);
				})
				.finally(() => {
					useSaveStateStore().TRIGGER_RELOAD();
					usePanelStore().SET_READY(true);
				})
				.catch(() => {
					toast().toast({
						message: i18n.global.t("storePvwToasts.errorRemovingView"),
						title: i18n.global.t("toastGeneralError"),
						type: "error",
						dismissable: false
					});
				});
		},
		async PVW_APPLICATION_STATE_RESET_VIEWS({
			viewToReset,
			viewToDelete
		}: {
			viewToReset: any;
			viewToDelete: any;
		}) {
			if (this.client != null) {
				return this.client
					.getRemote()
					.DataCloud.resetViews(viewToReset, viewToDelete)
					.catch((err: any) => {
						if (err && !err.message.includes("connection not open")) {
							toast().toast({
								message: i18n.global.t("storePvwToasts.errorResetViews"),
								title: i18n.global.t("toastGeneralError"),
								type: "error",
								dismissable: false
							});
						}
					});
			}
		},
		// ------------------------------------------------------------------------
		async PVW_SET_FIELD_LIST({ layerName, viewId, fieldList }: any) {
			// Sending hidden field to paraview to activate the labels
			fieldList.push("__HOLEID__");
			if (layerName.toLowerCase().indexOf("survey") > -1) return;
			return this.client
				.getRemote()
				.DataCloud.setFieldList(layerName, viewId, fieldList)
				.catch(() => {
					toast().toast({
						message: i18n.global.t("storePvwToasts.errorSettingFieldList"),
						title: i18n.global.t("toastGeneralError"),
						type: "error",
						dismissable: false
					});
				});
		},
		async PVW_COLOR_BY_UPDATE({
			layerName,
			viewId,
			field,
			fieldList,
			colorRange,
			colorType,
			fieldLabel,
			presetName,
			preset,
			presetInvert,
			annotations,
			rgbPoints,
			indexedColors,
			indexedOpacities,
			forceCategoricalComputation,
			useLogScale
		}: {
			layerName: any;
			viewId: any;
			field: any;
			fieldList: any;
			colorRange: any;
			colorType: any;
			fieldLabel: any;
			presetName: any;
			preset: any;
			presetInvert: any;
			annotations: any;
			rgbPoints: any;
			indexedColors: any;
			indexedOpacities: any;
			forceCategoricalComputation: any;
			useLogScale: any;
		}) {
			return this.client
				.getRemote()
				.DataCloud.updateColorBy(layerName, viewId, {
					fieldList,
					field,
					colorRange,
					colorType,
					fieldLabel,
					presetName,
					preset,
					presetInvert,
					annotations,
					rgbPoints,
					indexedColors,
					indexedOpacities,
					forceCategoricalComputation,
					useLogScale
				})
				.catch(() => {
					toast().toast({
						message: i18n.global.t("storePvwToasts.errorUpdatingColor"),
						title: i18n.global.t("toastGeneralError"),
						type: "error",
						dismissable: false
					});
				});
		},
		async PVW_REPRESENTATION_UPDATE_PROPERTY({
			layerName,
			viewId,
			type,
			name,
			value
		}: {
			layerName: string;
			viewId: string;
			type: string;
			name: string;
			value: any;
		}) {
			return this.client
				.getRemote()
				.DataCloud.updateRepresentationProperty(layerName, viewId, type, name, value)
				.catch(() => {
					toast().toast({
						message: i18n.global.t("storePvwToasts.errorUpdatingProp"),
						title: i18n.global.t("toastGeneralError"),
						type: "error",
						dismissable: false
					});
				});
		},
		// ========
		// Threshold Filtering
		// ========
		async PVW_FILTER_THRESHOLD_ADD({
			layerName,
			viewId,
			field,
			location,
			dataRange
		}: {
			layerName: any;
			viewId: any;
			field: any;
			location: any;
			dataRange: any;
		}) {
			return this.client
				.getRemote()
				.DataCloud.addThresholdFilter(layerName, viewId, field, location, dataRange)
				.catch(() => {
					toast().toast({
						message: i18n.global.t("storePvwToasts.errorAddFilter"),
						title: i18n.global.t("toastGeneralError"),
						type: "error",
						dismissable: false
					});
				});
		},
		// ------------------------------------------------------------------------
		PVW_FILTER_THRESHOLD_REMOVE({
			layerName,
			viewId,
			filterId
		}: {
			layerName: any;
			viewId: string | number;
			filterId: string | number;
		}) {
			return this.client
				.getRemote()
				.DataCloud.removeThresholdFilter(layerName, viewId, filterId)
				.catch(() => {
					toast().toast({
						message: i18n.global.t("storePvwToasts.errorRemoveFilter"),
						title: i18n.global.t("toastGeneralError"),
						type: "error",
						dismissable: false
					});
				});
		},
		// ------------------------------------------------------------------------
		async PVW_FILTER_THRESHOLD_UPDATE({
			layerName,
			viewId,
			filter
		}: {
			layerName: any;
			viewId: string | number;
			filter: any;
		}) {
			return this.client
				.getRemote()
				.DataCloud.updateThresholdFilter(layerName, viewId, filter)
				.catch(() => {
					toast().toast({
						message: i18n.global.t("storePvwToasts.errorUpdateFilter"),
						title: i18n.global.t("toastGeneralError"),
						type: "error",
						dismissable: false
					});
				});
		},
		//======================================================================================
		//======================================================================================
		//======================================================================================
		// Gizmos
		//======================================================================================
		//======================================================================================
		//======================================================================================

		async PVW_RESET_CAMERA({ viewId, bounds }: { viewId: string | number; bounds: any }) {
			const viewStates = views().SSA_VIEW_STATES;
			if (viewStates[viewId].linked) {
				for (let i = 0; i < Object.keys(viewStates).length; i++) {
					if (viewStates[Object.keys(viewStates)[i]].linked) {
						this.client
							.getRemote()
							.DataCloud.resetCamera(Object.keys(viewStates)[i], bounds)
							.catch(() => {
								toast().toast({
									message: i18n.global.t("storePvwToasts.errorResetCamera"),
									title: i18n.global.t("toastGeneralError"),
									type: "error",
									dismissable: false
								});
							});
					}
				}
			} else {
				await this.client
					.getRemote()
					.DataCloud.resetCamera(viewId, bounds)
					.catch(() => {
						toast().toast({
							message: i18n.global.t("storePvwToasts.errorResetCameraProblem"),
							title: i18n.global.t("toastGeneralError"),
							type: "error",
							dismissable: false
						});
					});
			}
		},
		async PVW_SET_VIEW_UP({
			viewId,
			viewType,
			northPole
		}: {
			viewId: string | number;
			viewType: string;
			northPole: boolean;
		}) {
			const viewStates = views().SSA_VIEW_STATES;

			if (viewStates[viewId].linked) {
				for (let i = 0; i < Object.keys(viewStates).length; i++) {
					if (viewStates[Object.keys(viewStates)[i]].linked) {
						await this.client
							.getRemote()
							.DataCloud.setViewUp(
								Object.keys(viewStates)[i],
								viewType,
								undefined,
								northPole
							)
							.catch(() => {
								toast().toast({
									message: i18n.global.t("storePvwToasts.errorSettingViewPos"),
									title: i18n.global.t("toastGeneralError"),
									type: "error",
									dismissable: false
								});
							});
					}
				}
			} else {
				await this.client
					.getRemote()
					.DataCloud.setViewUp(viewId, viewType, undefined, northPole)
					.catch(() => {
						toast().toast({
							message: i18n.global.t("storePvwToasts.errorSettingViewPos"),
							title: i18n.global.t("toastGeneralError"),
							type: "error",
							dismissable: false
						});
					});
			}
		},
		// ------------------------------------------------------------------------
		async PVW_SET_CAMERA_CENTER({ viewId, center }: { viewId: string; center: any }) {
			return this.client
				.getRemote()
				.DataCloud.setCameraCenter(viewId, center)
				.catch(() => {
					toast().toast({
						message: i18n.global.t("storePvwToasts.errorSettingCamCenter"),
						title: i18n.global.t("toastGeneralError"),
						type: "error",
						dismissable: false
					});
				});
		},
		// ------------------------------------------------------------------------
		async PVW_VIEW_STATE_UPDATE({ viewId, changeSet }: { viewId: string; changeSet: any }) {
			return this.client
				.getRemote()
				.DataCloud.updateViewState(viewId, changeSet)
				.catch(() => {
					toast().toast({
						message: i18n.global.t("storePvwToasts.errorUpdateViewState"),
						title: i18n.global.t("toastGeneralError"),
						type: "error",
						dismissable: false
					});
				});
		},
		async PVW_VIEW_LINK({ viewId, link }: { viewId: string; link: any }) {
			return this.client
				.getRemote()
				.DataCloud.updateViewLink(viewId, link)
				.catch(() => {
					toast().toast({
						message: i18n.global.t("storePvwToasts.errorUpdateViewLink"),
						title: i18n.global.t("toastGeneralError"),
						type: "error",
						dismissable: false
					});
				});
		},
		// ------------------------------------------------------------------------
		//  SLICING
		// ------------------------------------------------------------------------
		PVW_FILTER_SLICE_ENABLE({ layerName, viewId }: { layerName: string; viewId: any }) {
			return this.client
				.getRemote()
				.DataCloud.enableSliceFilter(layerName, viewId)
				.catch(() => {
					toast().toast({
						message: i18n.global.t("storePvwToasts.errorEnableSlice"),
						title: i18n.global.t("toastGeneralError"),
						type: "error",
						dismissable: false
					});
				});
		},
		PVW_FILTER_SLICE_DISABLE({ layerName, viewId }: { layerName: string; viewId: any }) {
			return this.client
				.getRemote()
				.DataCloud.disableSliceFilter(layerName, viewId)
				.catch(() => {
					toast().toast({
						message: i18n.global.t("storePvwToasts.errorDisableSlice"),
						title: i18n.global.t("toastGeneralError"),
						type: "error",
						dismissable: false
					});
				});
		},
		// ------------------------------------------------------------------------
		PVW_FILTER_SLICE_ADD({ layerName, viewId }: { layerName: string; viewId: any }) {
			return this.client
				.getRemote()
				.DataCloud.addSliceFilter(layerName, viewId)
				.catch(() => {
					toast().toast({
						message: i18n.global.t("storePvwToasts.errorAddSlice"),
						title: i18n.global.t("toastGeneralError"),
						type: "error",
						dismissable: false
					});
				});
		},
		// ------------------------------------------------------------------------
		PVW_FILTER_SLICE_REMOVE({
			layerName,
			viewId,
			filterId
		}: {
			layerName: string;
			viewId: string;
			filterId: any;
		}) {
			return this.client
				.getRemote()
				.DataCloud.removeSliceFilter(layerName, viewId, filterId)
				.catch(() =>
					toast().toast({
						message: i18n.global.t("storePvwToasts.errorRemoveSlice"),
						title: i18n.global.t("toastGeneralError"),
						type: "error",
						dismissable: false
					})
				);
		},
		// ------------------------------------------------------------------------
		PVW_FILTER_SLICE_UPDATE({
			layerName,
			viewId,
			filter
		}: {
			layerName: string;
			viewId: string;
			filter: any;
		}) {
			return this.client
				.getRemote()
				.DataCloud.updateSliceFilter(layerName, viewId, filter)
				.catch(() =>
					toast().toast({
						message: i18n.global.t("storePvwToasts.errorUpdateSlice"),
						title: i18n.global.t("toastGeneralError"),
						type: "error",
						dismissable: false
					})
				);
		},
		// ------------------------------------------------------------------------
		// CLIPPPING
		// ------------------------------------------------------------------------
		async PVW_CLIP_UPDATE({
			layerName,
			viewId,
			options
		}: {
			layerName: string;
			viewId: string;
			options: any;
		}) {
			return this.client
				.getRemote()
				.DataCloud.updateClip(layerName, viewId, options)
				.catch(console.error);
		},
		async PVW_CLIP_TO_LAYERS({
			layerName,
			viewId,
			layersToClip,
			options = { enable: true, visible: true }
		}: {
			layerName: string;
			viewId: string;
			layersToClip: string[];
			options?: { enable: boolean; visible: boolean };
		}) {
			console.log(options);
			return this.client
				.getRemote()
				.DataCloud.applyClipToLayers(layerName, viewId, layersToClip, options)
				.catch(console.error);
		},
		//PICKING
		// ------------------------------------------------------------------------
		// Picking
		// ------------------------------------------------------------------------
		PVW_USER_EDITS_UPDATE({
			layerName,
			viewId,
			active
		}: {
			layerName: any;
			viewId: any;
			active: any;
		}): any {
			return this.client
				.getRemote()
				.DataCloud.updateUserEdits(layerName, viewId, active)
				.catch(() => {
					return;
				});
		},
		// ------------------------------------------------------------------------
		PVW_EXPORT_SELECTION_UPDATE({
			layerName,
			viewId,
			active
		}: {
			layerName: any;
			viewId: any;
			active: any;
		}): any {
			return this.client
				.getRemote()
				.DataCloud.updateExportSelection(layerName, viewId, active)
				.catch(() => {
					return;
				});
		},
		// ------------------------------------------------------------------------
		PVW_EVENT_PICK({ layerName, event }: { layerName: any; event: any }): any {
			return this.client
				.getRemote()
				.DataCloud.pick({ ...event }, layerName)
				.catch(() => {
					return;
				});
		},
		// ------------------------------------------------------------------------
		PVW_PICK_AREA({
			viewId,
			layerName,
			mode,
			area,
			fullHole
		}: {
			viewId: any;
			layerName: any;
			mode: any;
			area: any;
			fullHole: any;
		}): any {
			return this.client
				.getRemote()
				.DataCloud.pickArea(viewId, area, layerName, mode, fullHole)
				.catch(() => {
					return;
				});
		},
		// ------------------------------------------------------------------------
		PVW_PICK_POLYGON({
			viewId,
			layerName,
			mode,
			points,
			fullHole
		}: {
			viewId: any;
			layerName: any;
			mode: any;
			points: any;
			fullHole: any;
		}): any {
			return this.client
				.getRemote()
				.DataCloud.pickPolygon(viewId, points, layerName, mode, fullHole)
				.catch(() => {
					return;
				});
		},
		// ------------------------------------------------------------------------
		PVW_CLEAR_PICK_SELECTION({ viewId, layerName }: { viewId: any; layerName: any }) {
			return this.client
				.getRemote()
				.DataCloud.clearPickSelection(viewId, layerName)
				.catch(() => {
					return;
				});
		},
		// ------------------------------------------------------------------------
		PVW_APPLY_CHANGESET({
			viewId,
			layerName,
			field,
			value
		}: {
			viewId: string;
			layerName: any;
			field: any;
			value: any;
		}): any {
			return this.client
				.getRemote()
				.DataCloud.applyChangeset(viewId, layerName, field, value)
				.then((changesets: any) => {
					if (changesets) {
						layer().SSA_LAYER_CHANGESETS_SET({
							layerName,
							changesets
						});
					}
					return changesets;
				})
				.catch(() => {
					return;
				});
		},
		// ------------------------------------------------------------------------
		PVW_REMOVE_LAST_CHANGESET({ viewId, layerName }: { viewId: string; layerName: any }): any {
			return this.client
				.getRemote()
				.DataCloud.removeLastChangeset(viewId, layerName)
				.then((changesets: any) => {
					if (changesets) {
						// commit('SSA_LAYER_CHANGESETS_SET', {
						//   layerName,
						//   changesets,
						// });
					}
					return changesets;
				})
				.catch(() => {
					return;
				});
		},
		// ------------------------------------------------------------------------
		// Color presets
		// ------------------------------------------------------------------------
		PVW_FETCH_PRESET_IMAGE({
			presetName,
			invert = false
		}: {
			presetName: string;
			invert: boolean;
		}) {
			return this.client
				.getRemote()
				.DataCloud.getPresetImage(presetName, 128, invert)
				.catch(() => {
					toast().toast({
						message: i18n.global.t("storePvwToasts.errorFetchImage"),
						title: i18n.global.t("toastGeneralError"),
						type: "error",
						dismissable: false
					});
				});
		},
		// ------------------------------------------------------------------------
		async PVW_VIEW_LAYER_ACTIVATE({
			viewId,
			layerName
		}: {
			viewId: string;
			layerName: string;
		}) {
			return this.client
				.getRemote()
				.DataCloud.updateActiveLayerInView(viewId, layerName)
				.catch(() => {
					console.log("there was a problem activating layer");
					//toast().toast({message:'There was a problem activating layer.',title:'An error occured.', type:'error', dismissable:false})
				});
		},
		// ------------------------------------------------------------------------
		// Application state
		// ------------------------------------------------------------------------
		async PVW_APPLICATION_STATE_GET() {
			if (this.client == null) {
				return;
			}
			return this.client
				.getRemote()
				.DataCloud.getApplicationState()
				.catch(() => {
					toast().toast({
						message: i18n.global.t("storePvwToasts.errorGetState"),
						title: i18n.global.t("toastGeneralError"),
						type: "error",
						dismissable: false
					});
				});
		},
		// ------------------------------------------------------------------------
		async PVW_APPLICATION_STATE_SET(appState: any) {
			return this.client
				.getRemote()
				.DataCloud.setApplicationState(appState)
				.catch((e: any) => {
					console.log(e);
					toast().toast({
						message: i18n.global.t("storePvwToasts.errorSetState"),
						title: i18n.global.t("toastGeneralError"),
						type: "error",
						dismissable: false
					});
				});
		},
		// ------------------------------------------------------------------------
		async PVW_CLONE_VIEW() {
			const viewToClone = active().view;
			//   saveState.SSA_APPLICATION_STATE_LOADING_SET(true);
			if (viewToClone === "") return;
			return this.client
				.getRemote()
				.DataCloud.cloneView(viewToClone)
				.then((viewIds: string[]) => {
					const newId = viewIds.find((el) => views().SSA_VIEW_IDS.indexOf(el) === -1);
					views().SSA_VIEW_IDS_SET(viewIds);
					// set any new view to active. Default colormap depends on this.
					if (newId) {
						const layerSelections = JSON.parse(
							JSON.stringify(useHierarchyStore().HIERARCHY_LAYER_SELECTIONS)
						);
						const selectionToClone =
							useHierarchyStore().HIERARCHY_LAYER_SELECTIONS[viewToClone];
						const stateToClone = useHierarchyStore().HIERARCHY_STATE[viewToClone];
						const layerStates = JSON.parse(
							JSON.stringify(useHierarchyStore().HIERARCHY_STATE)
						);

						const activeSelections = active().selectedDatasets[viewToClone];
						const datasetSelections = active().selectedDatasets;
						datasetSelections[newId] = activeSelections;
						const layerByView = active().layerByView[viewToClone];
						const layerByViewNew = active().layerByView;
						layerByViewNew[newId] = layerByView;
						const activeDatasetsNew = active().dataset[viewToClone];
						const activeDatasets = active().dataset;
						activeDatasets[newId] = activeDatasetsNew;

						layerSelections[newId] = selectionToClone;
						layerStates[newId] = stateToClone;

						useHierarchyStore().SET_HIERARCHY_STATE(layerStates, layerSelections);
						useDisplayPaneStore().CLONE_ACTIVE_DISPLAY_STATE(newId);
						useFiltersStore().SSA_CLONE_FILTER_STATES_FOR_VIEW(viewToClone, newId);
						// useHierarchyStore().LOADED_PIECES_SET({ id: newId, payload: getters.LOADED_PIECES[viewId] }); // need to change
						active().SSA_ACTIVE_VIEW_SET(newId);
						active().SSA_SET_DATASET(activeDatasets);
						active().SSA_ACTIVE_LAYER_BY_VIEW_SET(layerByViewNew);
						active().SSA_SET_SELECTED_DATASETS(datasetSelections);
					}
					//   commit('SSA_APPLICATION_STATE_LOADING_SET', false);
					return newId;
				})
				.catch((e: any) => {
					console.log(e);
					toast().toast({
						message: i18n.global.t("storePvwToasts.errorCloneView"),
						title: i18n.global.t("toastGeneralError"),
						type: "error",
						dismissable: false
					});
				});
		}
	}
});
